import { canonicalJson } from './canonicalJson';
import { normaliseEventInput } from './eventInput';
import { questionDefaults } from './questionDefaults';
import type { EventInput, Field, PdfDocumentDefinition } from './types';

/** What differs between the published event and the editor's draft, as short lines such as
 * “Event details: dates, description”. Empty when publishing would change nothing families see. Both sides are compared
 * as publishing saves them, so an edit that is undone, or one only in spacing, isn't a change. */
export function publishChanges(published: EventInput, draft: EventInput): string[] {
  const before = normaliseEventInput(published), after = normaliseEventInput(draft);
  const same = (a: unknown, b: unknown) => canonicalJson(a) === canonicalJson(b);
  const lines: string[] = [];

  const details = ([
    ['event name', e => e.name],
    ['dates', e => e.dates],
    ['description', e => e.description],
    ['stake or ward', e => [e.level, e.stakeName, e.wardName]],
    ['ward list', e => e.wardNames],
    ['leader', e => [e.leaderName, e.leaderPhone, e.leaderEmail]],
    ['parent signature age', e => e.adultAge ?? 18],
  ] as [string, (e: EventInput) => unknown][]).filter(([, value]) => !same(value(before), value(after))).map(([name]) => name);
  if (details.length) lines.push(`Event details: ${details.join(', ')}`);

  const questions = listChanges(before.fields, after.fields, field => field.id);
  if (questions) lines.push(`Questions: ${questions}`);

  // Publishing gives a form's own questions their defaults, such as “Untitled question”, so the draft's are compared with them too.
  const forms = (e: EventInput) => (e.documentForms ?? []).map(d => ({ ...d, questions: (d.questions ?? []).map(questionDefaults) }));
  const beforeForms = forms(before), afterForms = forms(after);
  const name = (d: PdfDocumentDefinition) => d.title.trim() || 'Untitled form';
  const formLines = [
    ...afterForms.filter(d => !beforeForms.some(p => p.id === d.id)).map(d => `${name(d)} (added)`),
    ...afterForms.filter(d => { const p = beforeForms.find(p => p.id === d.id); return p && !same(p, d); }).map(d => `${name(d)} (changed)`),
    ...beforeForms.filter(p => !afterForms.some(d => d.id === p.id)).map(p => `${name(p)} (removed)`),
  ];
  const sameForms = beforeForms.filter(p => afterForms.some(d => d.id === p.id)).map(p => p.id);
  if (!formLines.length && !same(sameForms, afterForms.filter(d => sameForms.includes(d.id)).map(d => d.id))) formLines.push('reordered');
  if (formLines.length) lines.push(`Forms: ${formLines.join(', ')}`);
  return lines;
}

/** “2 changed, 1 added”, or “reordered” when only the order differs; empty when nothing does. */
function listChanges(before: Field[], after: Field[], id: (field: Field) => string): string {
  const same = (a: unknown, b: unknown) => canonicalJson(a) === canonicalJson(b);
  const added = after.filter(f => !before.some(p => id(p) === id(f))).length;
  const removed = before.filter(p => !after.some(f => id(f) === id(p))).length;
  const changed = after.filter(f => { const p = before.find(p => id(p) === id(f)); return p && !same(p, f); }).length;
  const parts = [changed && `${changed} changed`, added && `${added} added`, removed && `${removed} removed`].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return same(before.map(id), after.map(id)) ? '' : 'reordered';
}
